import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/lib/api';
import { Icon } from '@/components/Icon';
import { SocialLinks } from '@/components/SocialLinks';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setAuth } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { email, password });
      setAuth(response.data.data);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
      <div className="w-full max-w-md mx-4 sm:mx-0 p-8 bg-[#12121a] rounded-2xl border border-white/10">
        <div className="text-center mb-8">
          <a href="https://r3converse.com" target="_blank" rel="noopener noreferrer">
            <a href="https://r3converse.com" target="_blank" rel="noopener noreferrer">
            <img src="/logo-r3converse.svg" alt="r3Converse" className="h-12 w-auto mx-auto mb-4" />
          </a>
          </a>
          <p className="text-gray-400 mt-2">{t('login.title')}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('login.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
              placeholder={t('login.emailPlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('login.password')}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
                placeholder={t('login.passwordPlaceholder')}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showPassword ? (
                  <Icon name="eye" className="text-lg" />
                ) : (
                  <Icon name="eye-slash" className="text-lg" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[#006cd4] text-black font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
          >
            {loading ? t('login.signingIn') : t('login.signIn')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          {t('login.noAccount')}{' '}
          <a href="/register" className="text-[#006cd4] hover:underline">
            {t('login.getStarted')}
          </a>
        </p>

        <SocialLinks />
      </div>
    </div>
  );
};
